/**
 * Dashboard Routes
 * Protected routes for authenticated users with lazy-loaded components
 */

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { DashboardLayout } from '@/layouts/dashboard';
import { LoginAuthGuard } from '@/routes/components/login-auth-guard';
import { AdminRouteGuard } from '@/routes/components/admin-route-guard';
import type { RouteObject } from 'react-router-dom';

// Lazy load pages
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const TransactionsPage = lazy(() => import('@/pages/transactions/TransactionsPage'));
const TransactionDetailPage = lazy(() => import('@/pages/transactions/TransactionDetailPage'));
const CreateExpensePage = lazy(() => import('@/pages/transactions/CreateExpensePage'));
const CreateIncomePage = lazy(() => import('@/pages/transactions/CreateIncomePage'));
const ContactsPage = lazy(() => import('@/pages/contacts/ContactsPage'));
const InventoryPage = lazy(() => import('@/pages/inventory/InventoryPage'));
const CompositeItemsPage = lazy(() => import('@/pages/inventory/composites/CompositeItemsPage'));
const EmployeesPage = lazy(() => import('@/pages/employees/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('@/pages/employees/EmployeeDetailPage'));
const AddEmployeePage = lazy(() => import('@/pages/employees/AddEmployeePage'));
const EditEmployeePage = lazy(() => import('@/pages/employees/EditEmployeePage'));
const BranchesPage = lazy(() => import('@/pages/branches/BranchesPage'));
const UsersPage = lazy(() => import('@/pages/settings/users/UsersPage'));
const CategoriesPage = lazy(() => import('@/pages/settings/CategoriesPage'));
const AuditLogsPage = lazy(() => import('@/pages/settings/AuditLogsPage'));
const GeneralSettingsPage = lazy(() => import('@/pages/settings/GeneralSettingsPage'));
const PayablesPage = lazy(() => import('@/pages/payables/PayablesPage'));
const ReceivablesPage = lazy(() => import('@/pages/receivables/ReceivablesPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));

export const dashboardRoutes: RouteObject[] = [
  {
    path: '/',
    element: <LoginAuthGuard />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          // Redirect root to dashboard
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },

          // Dashboard
          {
            path: 'dashboard',
            element: <DashboardPage />,
          },

          // Transactions
          {
            path: 'transactions',
            element: <TransactionsPage />,
          },
          {
            path: 'transactions/create/expense',
            element: <CreateExpensePage />,
          },
          {
            path: 'transactions/create/income',
            element: <CreateIncomePage />,
          },
          {
            path: 'transactions/:id',
            element: <TransactionDetailPage />,
          },
          {
            path: 'transactions/:id/edit',
            element: <TransactionDetailPage />,
          },

          // Contacts
          {
            path: 'contacts',
            element: <ContactsPage />,
          },

          // Inventory
          {
            path: 'inventory',
            element: <InventoryPage />,
          },
          {
            path: 'inventory/composites',
            element: <CompositeItemsPage />,
          },

          // Employees
          {
            path: 'employees',
            element: <EmployeesPage />,
          },
          {
            path: 'employees/new',
            element: <AddEmployeePage />,
          },
          {
            path: 'employees/:id',
            element: <EmployeeDetailPage />,
          },
          {
            path: 'employees/:id/edit',
            element: <EditEmployeePage />,
          },

          // Settings (Admin only)
          {
            path: 'settings',
            element: <AdminRouteGuard />,
            children: [
              {
                path: 'users',
                element: <UsersPage />,
              },
              {
                path: 'branches',
                element: <BranchesPage />,
              },
              {
                path: 'categories',
                element: <CategoriesPage />,
              },
              {
                path: 'audit',
                element: <AuditLogsPage />,
              },
              {
                path: 'general',
                element: <GeneralSettingsPage />,
              },
            ],
          },

          // Payables & Receivables
          {
            path: 'payables',
            element: <PayablesPage />,
          },
          {
            path: 'receivables',
            element: <ReceivablesPage />,
          },

          // Reports
          {
            path: 'reports',
            element: <ReportsPage />,
          },
        ],
      },
    ],
  },
];
